import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

export function AppHeader({ scriptTitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isCollaborator = user?.account_type === "collaborator";
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: "/app", label: "Scripts", testid: "scripts" },
    ...(!isCollaborator ? [{ to: "/app/boards", label: "Boards", testid: "boards" }] : []),
    ...(!isCollaborator ? [{ to: "/app/coach", label: "Coach", testid: "coach" }] : []),
    ...(user?.role === "super_admin" || user?.role === "admin" ? [{ to: "/admin", label: "Admin", testid: "admin" }] : []),
    { to: "/account", label: "Account", testid: "account" },
  ];

  return (
    <header className="sticky top-0 z-30 bg-[var(--canvas)]/85 backdrop-blur border-b border-[var(--line)]" data-testid="app-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6 min-w-0">
          <Link to="/app" className="font-display text-xl tracking-tight whitespace-nowrap" data-testid="app-logo">
            New <span className="text-[var(--brand)]">·</span> Screenplay
          </Link>
          {scriptTitle && (
            <span className="text-[var(--ink-secondary)] text-sm hidden sm:inline truncate" data-testid="header-script-title">
              <span className="text-[var(--ink-muted)]">/</span> {scriptTitle}
            </span>
          )}
        </div>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-3 text-sm">
          {links.map((l) => (
            <Link key={l.testid} to={l.to} className="text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]" data-testid={`header-${l.testid}-link`}>{l.label}</Link>
          ))}
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]"
            data-testid="header-logout-btn"
          >
            Sign out
          </button>
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="sm:hidden p-2 -mr-1 rounded text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
          data-testid="header-mobile-menu-btn"
          aria-label="Menu"
        >
          {menuOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="sm:hidden fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
          <nav className="sm:hidden absolute right-3 top-full mt-1 z-40 bg-white border border-[var(--line)] rounded-lg shadow-xl py-1.5 min-w-[180px]" data-testid="header-mobile-menu">
            {links.map((l) => (
              <Link key={l.testid} to={l.to} onClick={() => setMenuOpen(false)} className="block px-4 py-2.5 text-sm text-[var(--ink-primary)] hover:bg-[var(--warm-accent)]" data-testid={`mobile-header-${l.testid}-link`}>{l.label}</Link>
            ))}
            <button
              onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
              className="block w-full text-left px-4 py-2.5 text-sm text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] border-t border-[var(--line)] mt-1 pt-2.5"
              data-testid="mobile-header-logout-btn"
            >
              Sign out
            </button>
          </nav>
        </>
      )}
    </header>
  );
}

export function MarketingHeader() {
  return (
    <header className="absolute top-0 left-0 right-0 z-30">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="font-display text-xl md:text-2xl tracking-tight" data-testid="marketing-logo">
          New <span className="text-[var(--brand)]">·</span> Screenplay
        </Link>
        <nav className="flex items-center gap-3 md:gap-5 text-sm">
          <Link to="/login" className="text-[var(--ink-secondary)] hover:text-[var(--ink-primary)] px-2 py-2" data-testid="nav-signin">Sign in</Link>
          <Link to="/signup" className="btn-brand text-sm" data-testid="nav-signup">Start writing</Link>
        </nav>
      </div>
    </header>
  );
}
